import React from 'react';
import { useParams, Link } from 'react-router-dom';
import Header from '../../components/layout/Header';
import Footer from '../../components/layout/Footer';
import NotificationBanner from '../../components/ui/NotificationBanner';
import PostCard from '../../components/ui/PostCard';
import { posts } from '../../data/posts';
import { formatDate } from '../../utils/helpers';
import { FaFacebookF, FaTwitter, FaLinkedinIn } from 'react-icons/fa';

const PostDetails = () => {
    const { id } = useParams();
    const post = posts.find(p => p.id === parseInt(id));
    const relatedPosts = posts.filter(p => p.id !== parseInt(id)).slice(0, 3);

    if (!post) {
        return (
            <>
                <Header />
                <NotificationBanner />
                <main className="min-h-screen bg-gray-50 pt-28 pb-20">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <h1 className="text-3xl font-bold text-gray-900 mb-4">Post Not Found</h1>
                            <p className="text-gray-600 mb-8">The post you're looking for doesn't exist.</p>
                            <Link
                                to="/resources"
                                className="inline-flex items-center text-primary hover:text-primary-dark font-medium"
                            >
                                <svg
                                    className="w-4 h-4 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 19l-7-7 7-7"
                                    />
                                </svg>
                                Back to Resources
                            </Link>
                        </div>
                    </div>
                </main>
                <Footer />
            </>
        );
    }

    const shareUrl = window.location.href;
    const shareTitle = post.title;

    return (
        <>
            <Header />
            <NotificationBanner />
            <main className="min-h-screen bg-gray-50">
                {/* Hero Section */}
                <section className="relative pt-28 pb-20">
                    <div className="absolute inset-0">
                        <img
                            src={post.image}
                            alt={post.title}
                            className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-r from-gray-900/90 to-gray-900/70"></div>
                    </div>

                    <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center">
                            <span className="inline-block bg-primary text-white px-4 py-1 rounded-full text-sm mb-4">
                                {post.category}
                            </span>
                            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
                                {post.title}
                            </h1>
                            <div className="flex items-center justify-center text-gray-200 space-x-4">
                                <span>{post.author}</span>
                                <span>•</span>
                                <span>{formatDate(post.date)}</span>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Content Section */}
                <section className="py-16">
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                        {/* Featured Image */}
                        <div className="mb-8 rounded-lg overflow-hidden shadow-lg">
                            <img
                                src={post.image}
                                alt={post.title}
                                className="w-full h-[400px] object-cover"
                            />
                        </div>

                        <div className="bg-white rounded-lg shadow-lg p-8">
                            <div className="prose prose-lg max-w-none">
                                {post.content.split('\n\n').map((paragraph, index) => (
                                    <p key={index} className="mb-6 text-gray-700">
                                        {paragraph}
                                    </p>
                                ))}
                            </div>

                            {/* Bottom Line */}
                            <div className="mt-12 pt-8 border-t border-gray-200">
                                <div className="flex flex-col items-center space-y-6">
                                    {/* Share Buttons */}
                                    <div className="flex items-center space-x-4">
                                        <span className="text-gray-600 font-medium">Share this post:</span>
                                        <a
                                            href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:bg-blue-700 transition-colors"
                                        >
                                            <FaFacebookF />
                                        </a>
                                        <a
                                            href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 rounded-full bg-sky-500 flex items-center justify-center text-white hover:bg-sky-600 transition-colors"
                                        >
                                            <FaTwitter />
                                        </a>
                                        <a
                                            href={`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(shareUrl)}&title=${encodeURIComponent(shareTitle)}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="w-10 h-10 rounded-full bg-blue-700 flex items-center justify-center text-white hover:bg-blue-800 transition-colors"
                                        >
                                            <FaLinkedinIn />
                                        </a>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Related Posts */}
                        <div className="mt-16">
                            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">
                                Related Posts
                            </h2>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                                {relatedPosts.map(relatedPost => (
                                    <PostCard key={relatedPost.id} post={relatedPost} />
                                ))}
                            </div>
                        </div>

                        {/* Back Button */}
                        <div className="mt-12 text-center">
                            <Link
                                to="/resources"
                                className="inline-flex items-center text-primary hover:text-primary-dark font-medium"
                            >
                                <svg
                                    className="w-4 h-4 mr-2"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M15 19l-7-7 7-7"
                                    />
                                </svg>
                                Back to Resources
                            </Link>
                        </div>
                    </div>
                </section>
            </main>
            <Footer />
        </>
    );
};

export default PostDetails; 